/**
 * The read pointer: the highest message `seq` this visitor has actually looked at.
 *
 * It has to survive a page load, and the reason is specific to a widget rather
 * than to an app. The panel is destroyed and recreated on EVERY navigation of the
 * customer's site — clicking from a product page to the basket reloads our iframe
 * from scratch. An in-memory pointer would reset there, and every reply the
 * visitor had already read would count as unread again, so the badge would come
 * back on every page they visited. That is worse than no badge: it trains the
 * visitor to ignore it.
 *
 * `sessionStorage`, matching the session cache in `api/session-store.ts` and for
 * the same reason: this is a public-computer-shaped audience, and neither the
 * token nor the fact that someone read a support reply should outlive the tab.
 */
import type { SeqString } from '../api/contract';
import { compareSeq, ZERO_SEQ } from '../model/transcript';

/**
 * KEYED BY CONVERSATION AS WELL AS BY WIDGET KEY — P2-52.
 *
 * It used to be one pointer per public key, which was right while a visitor had
 * exactly one conversation ever. It is wrong the moment `closed` became terminal
 * (ADR-0142): `seq` RESTARTS AT 1 in the new conversation, so a pointer left at
 * 14 by the old one marks the new conversation's first fourteen replies as
 * already read. The visitor is told nothing arrived while the panel filled up
 * behind the launcher.
 *
 * Nothing migrates the old key, and nothing needs to: this is `sessionStorage`,
 * so the worst an orphan can do is occupy a few bytes until the tab closes —
 * and a migration would have to guess which conversation that number belonged
 * to, which is the fact that was missing in the first place.
 */
function key(publicKey: string, conversationId: string): string {
  return `omni.widget.seen.${publicKey}.${conversationId}`;
}

/**
 * Storage access throws outright in some privacy modes rather than returning
 * null. A lost read pointer shows one badge too many; an exception here shows an
 * empty panel, so the failure is absorbed and the pointer starts at zero.
 *
 * `conversationId` is null before the visitor has a conversation at all. There
 * is nothing to have read then, and answering `ZERO_SEQ` is not a fallback — it
 * is the true answer, and it keeps the caller from having to hold a branch for
 * a state that has no pointer.
 */
export function readSeenSeq(publicKey: string, conversationId: string | null): SeqString {
  if (conversationId === null) {
    return ZERO_SEQ;
  }

  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(key(publicKey, conversationId));
  } catch (storageUnavailable) {
    if (storageUnavailable instanceof Error) {
      return ZERO_SEQ;
    }
    throw storageUnavailable;
  }

  // Validated as a decimal int8 before it is trusted, because everything else in
  // this app compares it with `BigInt()`, and `BigInt('12x')` throws. A value
  // someone hand-edited into storage must not be able to take the panel down.
  return raw !== null && /^\d+$/u.test(raw) ? raw : ZERO_SEQ;
}

/**
 * The pointer only ever moves forward: loading scrollback must not un-read a
 * reply. Forward WITHIN ONE CONVERSATION — the ratchet is per key, which is the
 * whole point of the key carrying the conversation id.
 */
export function writeSeenSeq(
  publicKey: string,
  conversationId: string | null,
  seq: SeqString,
): void {
  if (conversationId === null) {
    return;
  }

  try {
    if (compareSeq(seq, readSeenSeq(publicKey, conversationId)) <= 0) {
      return;
    }
    window.sessionStorage.setItem(key(publicKey, conversationId), seq);
  } catch (storageUnavailable) {
    if (!(storageUnavailable instanceof Error)) {
      throw storageUnavailable;
    }
  }
}
